import React, { useEffect, useMemo, useState } from 'react';
import TableContainer from 'Common/TableContainer';
import { Dropdown } from 'Common/Components/Dropdown';
import { MoreHorizontal, FileEdit, Trash2, Plus } from 'lucide-react';
import DeleteModal from 'Common/DeleteModal';
import Modal from 'Common/Components/Modal';
import Select from "react-select";
import { useFormik } from "formik";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import * as Yup from "yup";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useLoader } from "../../context/LoaderContext";
import {
  getExpenseList,
  addExpense,
  updateExpense,
  deleteExpense,
  getWalletList,
  getExpenseCategoryList
} from "../../helpers/fakebackend_helper";
import { ToastContainer } from 'react-toastify';

interface ExpenseDTO {
  name: string;
  vendor: string;
  amount: number;
  wallet: any;
  movementCategory: any;
  date: string;
  [key: string]: any;
}

const Expense = () => {
  const FlatpickrComponent = Flatpickr as unknown as React.ComponentType<any>;
  const [data, setData] = useState<ExpenseDTO[]>([]);
  const [eventData, setEventData] = useState<ExpenseDTO | null>(null);
  const [show, setShow] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  const [walletOptions, setWalletOptions] = useState<
    { value: number; label: string }[]
  >([]);

  const [CategoryOptions, setCategoryOptions] = useState<
    { value: number; label: number, code: string }[]
  >([]);

  const linkifyEntity = (entityName: string, id: number) => {
    return `/${entityName}s/${id}`;
  };

  useEffect(() => {
    const fetchWallets = async () => {
      try {
        const response = await getWalletList();
        const embedded = response.data?._embedded?.wallets;
        const options = embedded
          ? Object.values(embedded).map((wallet: any) => ({
              value: wallet.walletId,
              label: wallet.name + " - Saldo: Q" + wallet.balance.toFixed(2) // Display balance with 2 decimal places,
            }))
          : [];
          setWalletOptions(options);
      } catch (error) {
        toast.error("Error al obtener las carteras: " + error, { autoClose: 2000 });
      }
    };
    fetchWallets();
  }, []);

  useEffect(() => {
    const fetchExpenseCategory = async () => {
      try {
        const response = await getExpenseCategoryList();
        const embedded = response.data?._embedded?.movementCategories;
        const options = embedded
          ? Object.values(embedded).map((Category: any) => ({
              value: Category.movementCategoryId,
              label: Category.name,
              code: Category.code
            }))
          : [];
          setCategoryOptions(options);
      } catch (error) {
        toast.error("Error al obtener los tipos de movimientos: " + error, { autoClose: 2000 });
      }
    };
    fetchExpenseCategory();
  }, []);

  const fetchExpenses = async () => {
    showLoader();

    try {
      const params = {
        page: 0,
        size: 1000,
        sort: "movementId,desc"
      };

      const response = await getExpenseList(params);
      const categoryData = response.data?._embedded?.movements || [];
      setData(categoryData);
    } catch (error) {
      toast.error("Error al obtener los movimientos : " + error, { autoClose: 2000 });
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      amount: 0,
      vendor: '',
      date: '',
      wallet: null,
      movementCategory: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("El campo nombre es requerido"),
      amount: Yup.number().required("El campo monto es requerido"),
      vendor: Yup.string().required("El campo proveedor es requerido"),
      date: Yup.string().required("El campo fecha es requerido"),
      wallet: Yup.number().nullable().required("El campo cartera es requerido"),
      movementCategory: Yup.number().nullable().required("El campo tipo de movimiento es requerido"),
    }),

    onSubmit: async (values) => {
      showLoader();

      try {
        const payload = {
          name: values.name,
          amount: values.amount,
          vendor: values.vendor,
          date: values.date,
          wallet: values.wallet !== null ? linkifyEntity("wallet", values.wallet) : null,
          movementCategory: values.movementCategory !== null ? linkifyEntity("movementCategorie", values.movementCategory) : null,
        };

        if (isEdit && eventData?.Id) {
          await updateExpense(eventData.Id, { id: eventData.Id, ...payload });
          toast.success("Movimiento actualizado correctamente", { autoClose: 2000 });
        } else {
          await addExpense(payload);
          toast.success("Movimiento agregado correctamente", { autoClose: 2000 });
        }
        handleCloseModal();
        fetchExpenses();
      } catch (error) {
        toast.error("Error al guardar el Movimiento: " + error, { autoClose: 2000 });
      } finally {
        hideLoader();
      }
    }
  });

  useEffect(() => {
    if (isEdit && eventData) {
      formik.setValues({
        name: eventData.name || '',
        amount: eventData.amount || 0,
        vendor: eventData.vendor || '',
        date: eventData.date || '',
        wallet: eventData?.wallet?.walletId || '',
        movementCategory: eventData?.movementCategory?.movementCategoryId || ''
      });
    } else if (!isEdit && !eventData) {
      formik.resetForm();
    }
  }, [eventData, isEdit]);

  const handleCloseModal = () => {
    setShow(false);
    setEventData(null);
    setIsEdit(false);
    formik.resetForm();
  };

  const handleCreateClick = () => {
    setEventData(null);
    setIsEdit(false);
    formik.resetForm();
    setShow(true);
  };

  const onClickDelete = (cell: ExpenseDTO) => {
    setEventData(cell);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (eventData?.Id) {
      showLoader();

      try {
        await deleteExpense(eventData.Id);
        toast.success("Movimiento eliminado correctamente", { autoClose: 2000 });
        setDeleteModal(false);
        fetchExpenses();
      } catch (err) {
        toast.error("Error al eliminar el movimiento: " + err, { autoClose: 2000 });
      }
    }
  };

  const handleUpdateDataClick = (ele: ExpenseDTO) => {
    setEventData(ele);
    setIsEdit(true);
    setShow(true);
  };

  const columns = useMemo(() => [
      {
        header: "Nombre",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
    },
    {
        header: "Proveedor",
        accessorKey: "vendor",
        enableColumnFilter: false,
        enableSorting: false,
    },
    {
        header: "Monto",
        accessorKey: "amount",
        enableColumnFilter: false,
        enableSorting: false,
    },
    {
        header: "Categoria",
        accessorKey: "movementCategory.name",
        enableColumnFilter: false,
        enableSorting: false,
    },
    {
        header: "Cartera",
        accessorKey: "wallet.name",
        enableColumnFilter: false,
        enableSorting: false,
    },
    {
        header: "Fecha",
        accessorKey: "date",
        enableColumnFilter: false,
        enableSorting: false,
    },
    {
      header: "Acciones",
      cell: ({ row }: any) => (
        <div className="flex justify-center">
          <Dropdown className="relative">
            <Dropdown.Trigger className="flex items-center justify-center size-8 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-600 hover:text-white focus:outline-none">
              <MoreHorizontal className="size-4" />
            </Dropdown.Trigger>
            <Dropdown.Content className="absolute z-50 right-0 mt-2 w-40 bg-white rounded-md shadow-lg dark:bg-zink-600">
              <ul>
                <li>
                  <button
                    type="button"
                    onClick={() => handleUpdateDataClick(row.original)}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-zink-100 dark:hover:bg-zink-500"
                  >
                    <FileEdit className="inline size-4 mr-2" />
                    Editar
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => onClickDelete(row.original)}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-zink-100 dark:hover:bg-zink-500"
                  >
                    <Trash2 className="inline size-4 mr-2" />
                    Eliminar
                  </button>
                </li>
              </ul>
            </Dropdown.Content>
          </Dropdown>
        </div>
      ),
    }
  ], []);

  return (
    <div className='card'>
      <DeleteModal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onDelete={handleDelete}
      />

      <ToastContainer closeButton={false} limit={1} />

      <div className="card-body">
        <div className="flex justify-end mb-4">
          <button onClick={handleCreateClick} className="btn bg-custom-500 text-white">
            <Plus className="inline size-4 mr-1" /> Nuevo Movimiento
          </button>
        </div>
      </div>

      <div className='card-body'>
        <TableContainer
          isPagination={true}
          isSelect={true}
          isGlobalFilter={true}
          columns={columns}
          data={data}
          customPageSize={5}
          divclassName="my-2 col-span-12 overflow-x-auto lg:col-span-12"
          tableclassName="hover group dataTable w-full text-sm align-middle whitespace-nowrap no-footer"
          theadclassName="border-b border-slate-200 dark:border-zink-500"
          trclassName="group-[.stripe]:even:bg-slate-50 group-[.stripe]:dark:even:bg-zink-600 transition-all duration-150 ease-linear group-[.hover]:hover:bg-slate-50 dark:group-[.hover]:hover:bg-zink-600 [&.selected]:bg-custom-500 dark:[&.selected]:bg-custom-500 [&.selected]:text-custom-50 dark:[&.selected]:text-custom-50"
          thclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500 sorting px-3 py-4 text-slate-900 bg-slate-200/50 font-semibold text-left dark:text-zink-50 dark:bg-zink-600 dark:group-[.bordered]:border-zink-500"
          tdclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500"
          PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
        />
      </div>

      <Modal show={show} onHide={handleCloseModal} modal-center
        className="fixed inset-0 z-drawer flex items-center justify-center"
        dialogClassName="w-screen max-w-md bg-white shadow-xl rounded-xl dark:bg-zink-600"
      >
        <Modal.Header className="flex items-center justify-between px-6 py-4 border-b dark:border-zink-500">
          <Modal.Title className="text-lg font-semibold text-slate-900 dark:text-zink-100">
            {isEdit ? "Editar Gasto" : "Agregar Movimiento"}
          </Modal.Title>
          <button onClick={handleCloseModal} className="text-slate-400 hover:text-red-500 transition">
            ×
          </button>
        </Modal.Header>

        <Modal.Body className="p-6">
          <form onSubmit={formik.handleSubmit} className="space-y-5">
          <div className="md:col-span-1">
            <label htmlFor="name" className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              placeholder="Ingrese nombre o describa el gasto o ingreso"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500"
            />
            {formik.touched.name && formik.errors.name && (
              <p className="text-sm text-red-500 mt-1">{formik.errors.name}</p>
            )}
          </div>

          <div className="md:col-span-1">
            <label htmlFor="vendor" className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100">
              Proveedor <span className="text-red-500">*</span>
            </label>
            <input
              id="vendor"
              name="vendor"
              type="text"
              placeholder="Ingrese nombre del proveedor"
              value={formik.values.vendor}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500"
            />
            {formik.touched.vendor && formik.errors.vendor && (
              <p className="text-sm text-red-500 mt-1">{formik.errors.vendor}</p>
            )}
          </div>

          <div className="md:col-span-1">
            <label htmlFor="amount" className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100">
              Gasto <span className="text-red-500">*</span>
            </label>
            <input
              id="amount"
              name="amount"
              type="number"
              placeholder="Ingrese monto"
              value={formik.values.amount}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500"
            />
            {formik.touched.amount && formik.errors.amount && (
              <p className="text-sm text-red-500 mt-1">{formik.errors.amount}</p>
            )}
          </div>

          <div className="md:col-span-1">
            <label htmlFor="wallet" className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100">
              Cuenta <span className="text-red-500">*</span>
            </label>
            <Select
              name="wallet"
              value={walletOptions.find(opt => opt.value === formik.values.wallet) || null}
              onChange={(selected) => {
                if (selected) {
                  formik.setFieldValue('wallet', selected.value);
                }
              }}
              onBlur={formik.handleBlur}
              options={walletOptions}
              className="w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500"
            />
            {formik.touched.wallet && formik.errors.wallet && (
              <p className="text-sm text-red-500 mt-1">{formik.errors.wallet}</p>
            )}
          </div>

          <div className="md:col-span-1">
            <label htmlFor="movementCategory" className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100">
              Tipo de Movimiento <span className="text-red-500">*</span>
            </label>
            <Select
              name="movementCategory"
              value={CategoryOptions.find(opt => opt.value === formik.values.movementCategory) || null}

              onChange={(selected) => {
                if (selected) {
                  formik.setFieldValue('movementCategory', selected.value);
                }
              }}

              onBlur={formik.handleBlur}
              options={CategoryOptions}
              className="w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500"
            />
            {formik.touched.movementCategory && formik.errors.movementCategory && (
              <p className="text-sm text-red-500 mt-1">{formik.errors.movementCategory}</p>
            )}
          </div>

          <div className="md:col-span-1">
            <label htmlFor="date" className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100">
              Fecha del Gasto <span className="text-red-500">*</span>
            </label>
            <FlatpickrComponent
              id="date"
              name="date"
              placeholder="Ingrese fecha"
              options={{
                altInput: true,
                altFormat: "d M, Y",
                dateFormat: "Y-m-d"
              }}
              value={formik.values.date || ""}
              onChange={(date: any) =>
                formik.setFieldValue("date", moment(date[0]).format("YYYY-MM-DD"))
              }
              className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500"
            />
            {formik.touched.date && formik.errors.date && (
              <p className="text-sm text-red-500 mt-1">{formik.errors.date}</p>
            )}
          </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={handleCloseModal} className="btn bg-white text-red-500 border border-red-500 hover:bg-red-50 transition">
                Cancelar
              </button>
              <button
                type="submit"
                className="btn bg-custom-500 text-white hover:bg-custom-600 transition">
                  {isEdit ? "Actualizar" : "Agregar"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Expense;